import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { apiFetch } from '../bizsuite-app';
import { toast } from '../utils/toast';

/**
 * CompensationSchemaPanel — replaces the static "Salary Breakup" section
 * on the Employee → Compensation Details tab with a schema-driven panel.
 *
 * Flow:
 *   1. Load the picked Salary Schema's components (from /salary-schemas/:id)
 *   2. Call the resolver for this employee → master rates per component
 *   3. Load any saved employee_salary_structure → restore HR-entered values
 *   4. Per row: show component, master rate (L1/L2/none), monthly value
 *      that HR can override, and which "source" it ended up using.
 *
 * Save creates a new row in employee_salary_structures (closes any prior
 * open row with valid_to = yesterday). Validity dates are user-set; default
 * is today → null (open-ended).
 *
 * Simulate just re-runs the resolver and recomputes monthly values where
 * HR hasn't overridden — useful when the schema's masters have changed.
 *
 * Props:
 *   employeeId   — UUID, required
 *   schemaId     — UUID, the salary schema selected on the form
 *   ctcAnnual    — fallback base (used when a master returns rate_pct but
 *                  no fixed_value, value = ctcAnnual / 12 * rate%)
 *   currency     — display only
 *   readOnly     — disable inputs
 */
export default function CompensationSchemaPanel({ employeeId, schemaId, ctcAnnual, currency, readOnly }) {
  const [components, setComponents] = useState([]);
  const [resolved, setResolved]     = useState([]);
  const [structure, setStructure]   = useState(null);  // last saved row
  const [overrides, setOverrides]   = useState({});    // {component_id: monthly_value}
  const [validFrom, setValidFrom]   = useState(() => new Date().toISOString().slice(0, 10));
  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState(false);

  const monthlyBase = (Number(ctcAnnual) || 0) / 12;

  // Tiny safe formula evaluator — used as a fallback when the resolver
  // can't find a master rate row. Supports BARE numbers (treated as a
  // percentage of monthlyBase), and simple expressions referencing BASE,
  // MONTHLY, CTC, BASIC tokens. Allow-lists characters before eval to
  // ensure no funny business gets through.
  function evalFormula(formula, base) {
    if (formula == null) return null;
    const expr = String(formula).trim();
    if (!expr) return null;
    if (/^\d+(\.\d+)?$/.test(expr)) return base * Number(expr) / 100;
    try {
      const safe = expr.replace(/\b(BASE|MONTHLY|CTC|BASIC)\b/gi, String(base));
      if (!/^[\d+\-*/().\s]+$/.test(safe)) return null;
      // eslint-disable-next-line no-new-func
      const v = Function('"use strict"; return (' + safe + ');')();
      return Number.isFinite(v) ? v : null;
    } catch (_) { return null; }
  }
  const cur = currency || '₹';
  const fmt = (n) => `${cur} ${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

  const load = useCallback(async () => {
    if (!schemaId || !employeeId) {
      setComponents([]); setResolved([]); setStructure(null); setOverrides({});
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [schemaRes, resolveRes, structRes] = await Promise.all([
        apiFetch(`/api/salary-schemas/${schemaId}`),
        apiFetch(`/api/salary-schemas/${schemaId}/resolve?employee_id=${employeeId}`),
        apiFetch(`/api/employees/${employeeId}/salary-structure`),
      ]);
      if (schemaRes.ok)  setComponents((await schemaRes.json()).components || []);
      if (resolveRes.ok) setResolved((await resolveRes.json()).components  || []);
      if (structRes.ok) {
        const s = (await structRes.json()).structure || null;
        setStructure(s);
        if (s && Array.isArray(s.components)) {
          const map = {};
          for (const c of s.components) {
            if (c.component_id != null && c.monthly_value != null) {
              map[c.component_id] = c.monthly_value;
            }
          }
          setOverrides(map);
        } else {
          setOverrides({});
        }
      }
    } catch (_) { /* ignore */ }
    setLoading(false);
  }, [employeeId, schemaId]);
  useEffect(() => { load(); }, [load]);

  /* Build the row model: master rate from resolver + HR override (if any). */
  const rows = useMemo(() => {
    const rmap = {}; for (const r of resolved) rmap[r.component_id] = r;
    return components.map(c => {
      const r = rmap[c.id] || null;
      const hasMaster = r && !r.no_master && (r.rate_pct != null || r.fixed_value != null);
      let masterMonthly = null;
      let usedFormula = false;
      if (hasMaster) {
        if (r.rate_pct != null)         masterMonthly = monthlyBase * (Number(r.rate_pct) || 0) / 100;
        else if (r.fixed_value != null) masterMonthly = Number(r.fixed_value) || 0;
      } else {
        // No master rate found in L1/L2 — fall back to the schema's formula
        // (e.g. "BASE * 0.4" or "40" → 40% of base). Lets the design-time
        // default kick in instead of showing zero, matching what the user
        // configured on the schema itself.
        const formula = (r && r.formula) || c.formula;
        const v = evalFormula(formula, monthlyBase);
        if (v != null) {
          masterMonthly = v;
          usedFormula = true;
        }
      }
      const override = overrides[c.id];
      const hasOverride = override !== undefined && override !== '' && override !== null;
      const effective = hasOverride
        ? Number(override) || 0
        : (masterMonthly != null ? masterMonthly : 0);
      const source = hasOverride ? 'manual'
                    : (hasMaster ? 'master'
                       : usedFormula ? 'formula'
                                     : 'none');
      return {
        component_id: c.id,
        code: c.code,
        name: c.name,
        category: c.category,
        seq: c.seq,
        master_rate_pct: r ? r.rate_pct : null,
        master_match_level: r ? r.match_level : null,
        master_monthly: masterMonthly,
        override,
        effective,
        source,
      };
    });
  }, [components, resolved, overrides, monthlyBase]);

  const totals = useMemo(() => {
    let gross = 0, ded = 0, tax = 0, employer = 0;
    for (const row of rows) {
      const v = row.effective || 0;
      const cat = String(row.category || '');
      if (cat === 'Earning')              gross    += v;
      if (cat === 'Deduction')            ded      += v;
      if (cat === 'Tax')                  tax      += v;
      if (cat === 'EmployerContribution') employer += v;
    }
    return { gross, ded, tax, employer, net: gross - ded - tax, ctc: gross + employer };
  }, [rows]);

  function setOverride(componentId, value) {
    setOverrides(prev => {
      const next = { ...prev };
      if (value === '' || value === null || value === undefined) delete next[componentId];
      else next[componentId] = value;
      return next;
    });
  }

  async function simulate() {
    // Re-run the resolver and recompute. HR overrides stay intact.
    if (!schemaId || !employeeId) return;
    try {
      const r = await apiFetch(`/api/salary-schemas/${schemaId}/resolve?employee_id=${employeeId}`);
      if (r.ok) {
        setResolved((await r.json()).components || []);
        toast.success('Resolved using current masters');
      }
    } catch (_) { /* ignore */ }
  }

  async function save() {
    if (!schemaId || !employeeId) { toast.error('Pick a salary schema first'); return; }
    setSaving(true);
    try {
      const payload = {
        schema_id: schemaId,
        valid_from: validFrom || new Date().toISOString().slice(0, 10),
        components: rows.map(r => ({
          component_id: r.component_id,
          code: r.code,
          name: r.name,
          monthly_value: r.effective,
          source: r.source,
          match_level: r.master_match_level,
        })),
      };
      const res = await apiFetch(`/api/employees/${employeeId}/salary-structure`, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      if (res.ok) { toast.success('Compensation saved'); load(); }
      else        { const d = await res.json().catch(() => ({})); toast.error(d.error || 'Save failed'); }
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  }

  if (!schemaId) {
    return (
      <div style={emptyBox}>
        Pick a <strong>Salary Schema</strong> at the top to render the
        compensation breakdown for this employee.
      </div>
    );
  }
  if (loading) return <div style={{ color: '#94A3B8', fontSize: 12.5, padding: 12 }}>Loading compensation…</div>;
  if (components.length === 0) {
    return <div style={emptyBox}>The selected schema has no components.</div>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <div style={{ fontSize: 12, color: '#94A3B8' }}>
          Resolved using a <strong>2-level priority lookup</strong> · Monthly base{' '}
          <strong style={{ color: '#E5E7EB' }}>{fmt(monthlyBase)}</strong>
          {structure?.valid_from && (
            <> · Last saved <strong style={{ color: '#E5E7EB' }}>{String(structure.valid_from).slice(0, 10)}</strong></>
          )}
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#94A3B8' }}>
            Effective From
            <input type="date" value={validFrom} onChange={e => setValidFrom(e.target.value)}
              disabled={readOnly} style={inp} />
          </label>
          <button onClick={simulate} style={btnGhost} disabled={readOnly}>Simulate</button>
          <button onClick={save} disabled={readOnly || saving} style={btnPrimary}>
            {saving ? 'Saving…' : 'Save Compensation'}
          </button>
        </div>
      </div>

      <div style={{ overflowX: 'auto', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8 }}>
        <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: 12.5 }}>
          <thead>
            <tr>
              <th style={th}>#</th>
              <th style={th}>Component</th>
              <th style={th}>Category</th>
              <th style={{ ...th, textAlign: 'right' }}>Master Rate</th>
              <th style={{ ...th, textAlign: 'right' }}>Master Monthly</th>
              <th style={{ ...th, textAlign: 'right' }}>HR Override</th>
              <th style={{ ...th, textAlign: 'right' }}>Effective Monthly</th>
              <th style={th}>Source</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.component_id} style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                <td style={td}>{r.seq ?? '—'}</td>
                <td style={td}>
                  <div style={{ color: '#E5E7EB' }}>{r.name}</div>
                  {r.code && <div style={{ fontSize: 11, color: '#64748B' }}>{r.code}</div>}
                </td>
                <td style={{ ...td, color: '#94A3B8' }}>{r.category || '—'}</td>
                <td style={{ ...td, textAlign: 'right', fontFamily: 'ui-monospace, monospace',
                  color: r.master_rate_pct != null ? '#E5E7EB' : '#64748B' }}>
                  {r.master_rate_pct != null ? `${Number(r.master_rate_pct).toFixed(2)}%` : '—'}
                </td>
                <td style={{ ...td, textAlign: 'right', fontFamily: 'ui-monospace, monospace',
                  color: r.master_monthly != null ? '#E5E7EB' : '#64748B' }}>
                  {r.master_monthly != null ? fmt(r.master_monthly) : '—'}
                </td>
                <td style={{ ...td, textAlign: 'right' }}>
                  <input type="number" step="0.01"
                    value={r.override ?? ''}
                    placeholder={r.master_monthly != null ? Number(r.master_monthly).toFixed(0) : '—'}
                    onChange={e => setOverride(r.component_id, e.target.value)}
                    disabled={readOnly}
                    style={{ ...inp, width: 110, textAlign: 'right' }} />
                </td>
                <td style={{ ...td, textAlign: 'right', fontFamily: 'ui-monospace, monospace',
                  color: '#10B981', fontWeight: 600 }}>
                  {fmt(r.effective)}
                </td>
                <td style={td}>
                  {r.source === 'master'
                    ? <span style={{ ...pill, background: 'rgba(16,185,129,0.15)', color: '#10B981' }}>L{r.master_match_level || '?'}</span>
                    : r.source === 'manual'
                      ? <span style={{ ...pill, background: 'rgba(245,158,11,0.15)', color: '#F59E0B' }}>Manual</span>
                      : <span style={{ ...pill, background: 'rgba(239,68,68,0.15)', color: '#EF4444' }}>None</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10 }}>
        <Totl label="Monthly Gross"    value={totals.gross}    color="#10B981" />
        <Totl label="Deductions"       value={-totals.ded}     color="#EF4444" />
        <Totl label="Tax"              value={-totals.tax}     color="#F59E0B" />
        <Totl label="Net Monthly"      value={totals.net}      color="#00E0FF" big />
        <Totl label="Employer Cost"    value={totals.employer} color="#3B8FF4" />
        <Totl label="Total Monthly CTC" value={totals.ctc}     color="#BF5FFF" big />
      </div>
    </div>
  );
}

function Totl({ label, value, color, big }) {
  return (
    <div style={{
      background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)',
      borderRadius: 6, padding: '8px 10px',
    }}>
      <div style={{ fontSize: 10, color: '#94A3B8', marginBottom: 2, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
      <div style={{ color, fontSize: big ? 15 : 13, fontWeight: 700, fontFamily: 'ui-monospace, monospace' }}>
        ₹ {Number(value || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
      </div>
    </div>
  );
}

const emptyBox = { padding: 16, color: '#94A3B8', fontSize: 12.5, textAlign: 'center',
                   background: 'rgba(255,255,255,0.03)', border: '1px dashed rgba(255,255,255,0.10)', borderRadius: 8 };
const inp = { padding: '6px 10px', borderRadius: 5, border: '1px solid rgba(255,255,255,0.10)',
              background: 'rgba(15,23,42,0.6)', color: '#E5E7EB', fontSize: 12.5, outline: 'none' };
const btnPrimary = { padding: '8px 14px', borderRadius: 7, fontSize: 12.5, fontWeight: 600, border: 'none', cursor: 'pointer',
                     background: 'linear-gradient(135deg, #FF2D7B, #FF6B9D)', color: '#fff' };
const btnGhost   = { padding: '8px 12px', borderRadius: 7, fontSize: 12.5, fontWeight: 600,
                     border: '1px solid rgba(255,255,255,0.10)', background: 'rgba(255,255,255,0.04)',
                     color: '#E5E7EB', cursor: 'pointer' };

const th = { padding: '8px 10px', textAlign: 'left', background: 'rgba(255,255,255,0.04)',
              color: '#94A3B8', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px',
              borderBottom: '1px solid rgba(255,255,255,0.10)' };
const td = { padding: '7px 10px', verticalAlign: 'middle' };
const pill = { display: 'inline-block', padding: '2px 8px', borderRadius: 999,
               fontSize: 10.5, fontWeight: 700, letterSpacing: '0.4px' };
